/**
 * Kundenanzeige auf dem zweiten Windows-Bildschirm öffnen (Posiflex LM-3010E
 * u. ä. als erweiterter Desktop). Mit der Window-Management-API von Chrome/Edge
 * (`getScreenDetails`, Berechtigung „Fensterverwaltung“) wird das Fenster direkt
 * auf den kundenseitigen Bildschirm gelegt; ohne sie öffnet es normal und wird
 * von Hand hinübergezogen. Reine Auswahl-Logik ist getrennt testbar.
 */

export interface Bildschirm {
  availLeft: number;
  availTop: number;
  availWidth: number;
  availHeight: number;
  isPrimary: boolean;
  label?: string;
}

/** Aufruf der Anzeige, fest an diese Kasse gebunden. */
export function anzeigeUrl(kassenId: string): string {
  return `?anzeige=1&kasse=${encodeURIComponent(kassenId)}`;
}

/** Fenstername je Kasse: erneutes Öffnen holt dasselbe Fenster, zwei Kassen bekommen zwei. */
export function anzeigeFensterName(kassenId: string): string {
  return `bilanx-kundenanzeige-${kassenId.replace(/[^A-Za-z0-9_-]/g, '_')}`;
}

/**
 * Bildschirm für die Kundenanzeige: nicht der Bildschirm der Kasse, bevorzugt nicht
 * der Hauptbildschirm und bevorzugt 1024 × 768 (Posiflex-Kundenanzeige).
 */
export function waehleKundenBildschirm(
  bildschirme: readonly Bildschirm[],
  kasse: Bildschirm | null,
): Bildschirm | null {
  const gleich = (a: Bildschirm, b: Bildschirm): boolean =>
    a.availLeft === b.availLeft &&
    a.availTop === b.availTop &&
    a.availWidth === b.availWidth &&
    a.availHeight === b.availHeight;
  const andere = kasse === null ? [...bildschirme] : bildschirme.filter((b) => !gleich(b, kasse));
  if (andere.length === 0) {
    return null;
  }
  const rang = (b: Bildschirm): number =>
    (b.isPrimary ? 0 : 2) + (b.availWidth === 1024 && b.availHeight <= 768 ? 1 : 0);
  return [...andere].sort((a, b) => rang(b) - rang(a))[0] ?? null;
}

/** Fenstermerkmale für `window.open`: Popup über die ganze verfügbare Fläche. */
export function fensterMerkmale(b: Bildschirm | null): string {
  if (b === null) {
    return 'popup,width=1024,height=768';
  }
  return [
    'popup',
    `left=${String(b.availLeft)}`,
    `top=${String(b.availTop)}`,
    `width=${String(b.availWidth)}`,
    `height=${String(b.availHeight)}`,
  ].join(',');
}

export type OeffnenErgebnis =
  /** Fenster liegt auf dem zweiten Bildschirm. */
  | 'zweiter-bildschirm'
  /** Nur ein Bildschirm erkannt bzw. keine Bildschirmwahl möglich: Fenster von Hand verschieben. */
  | 'ohne-bildschirmwahl'
  /** Popup blockiert (Berechtigungsabfrage kostete die Klick-Berechtigung): noch einmal klicken. */
  | 'blockiert';

interface ScreenDetails {
  screens: readonly Bildschirm[];
  currentScreen: Bildschirm;
}

export interface FensterUmgebung {
  open(url: string, name: string, merkmale: string): unknown;
  getScreenDetails?: () => Promise<ScreenDetails>;
}

/** Öffnet die Kundenanzeige – aus einem Klick heraus aufrufen (Popup-Sperre). */
export async function oeffneKundenanzeige(
  kassenId: string,
  fenster: FensterUmgebung,
): Promise<OeffnenErgebnis> {
  let ziel: Bildschirm | null = null;
  if (typeof fenster.getScreenDetails === 'function') {
    try {
      const details = await fenster.getScreenDetails();
      ziel = waehleKundenBildschirm(details.screens, details.currentScreen);
    } catch {
      // Berechtigung verweigert oder nicht verfügbar: ohne Bildschirmwahl öffnen.
    }
  }
  const neu: unknown = fenster.open(
    anzeigeUrl(kassenId),
    anzeigeFensterName(kassenId),
    fensterMerkmale(ziel),
  );
  if (neu === null || neu === undefined) {
    return 'blockiert';
  }
  // Ein schon offenes Fenster gleichen Namens behält seine Lage: ausdrücklich verschieben.
  // Ergebnis „zweiter-bildschirm“ heißt: Platzierung angewiesen – ob der Browser sie
  // umgesetzt hat, prüft die Kassierkraft (Hinweis in der Einrichtung, Abnahme A2).
  const offen = neu as Partial<Pick<Window, 'moveTo' | 'resizeTo' | 'focus'>>;
  let verschoben = false;
  try {
    if (ziel !== null && typeof offen.moveTo === 'function') {
      offen.moveTo(ziel.availLeft, ziel.availTop);
      offen.resizeTo?.(ziel.availWidth, ziel.availHeight);
      verschoben = true;
    }
    offen.focus?.();
  } catch {
    verschoben = false;
  }
  return verschoben ? 'zweiter-bildschirm' : 'ohne-bildschirmwahl';
}

/**
 * Bindung der Anzeige an die Kasse: `?kasse=` im Aufruf muss zur Kasse passen, mit
 * der das Gerät verbunden ist. Ergebnis: Kassen-ID oder Fehlertext.
 */
export function anzeigeKasse(
  parameterKasse: string | null,
  geraeteKasse: string,
): { kassenId: string } | { fehler: string } {
  if (geraeteKasse === '') {
    return { fehler: 'Dieses Gerät ist mit keiner Kasse verbunden.' };
  }
  if (parameterKasse !== null && parameterKasse !== '' && parameterKasse !== geraeteKasse) {
    return {
      fehler: `Diese Anzeige gehört zu Kasse ${parameterKasse}, das Gerät ist aber mit Kasse ${geraeteKasse} verbunden.`,
    };
  }
  return { kassenId: geraeteKasse };
}
